/*
 * brd-2026-hero-slider.js
 *
 * Performance behaviour:
 *
 * - Videos have no src in the initial HTML.
 * - Only the ACTIVE slide receives a video src.
 * - Only the correct MOBILE or DESKTOP version receives a src.
 * - Inactive slide videos remain unloaded until visited.
 * - Loaded videos are paused rather than unloaded when leaving a slide,
 *   avoiding repeated downloads when the user returns.
 *
 * Swiper is loaded globally by layout/theme.liquid.
 */

(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';


  /* ----------------------------------------------------------
   * Wait for globally-loaded Swiper
   * ---------------------------------------------------------- */

  function whenSwiperReady(cb, attempt) {
    attempt = attempt || 0;

    if (typeof window.Swiper === 'function') {
      cb();
      return;
    }

    // ~10 second ceiling.
    if (attempt > 200) {
      return;
    }

    setTimeout(function () {
      whenSwiperReady(cb, attempt + 1);
    }, 50);
  }


  /* ----------------------------------------------------------
   * Responsive video helpers
   * ---------------------------------------------------------- */

  function isMobileViewport() {
    return (
      window.matchMedia &&
      window.matchMedia(MOBILE_QUERY).matches
    );
  }


  /*
   * Attach the real video URL only when we actually need it.
   *
   * Until this function runs, the <video> has:
   *
   *   data-src="..."
   *
   * but NO:
   *
   *   src="..."
   *
   * Therefore the browser cannot download the movie during
   * initial HTML parsing.
   */
  function ensureVideoLoaded(video) {
    if (!video) {
      return false;
    }

    if (video.dataset.brdVideoLoaded === '1') {
      return true;
    }

    var src = video.dataset.src;

    if (!src) {
      return false;
    }

    video.src = src;

    /*
     * load() is intentionally called only after src has been assigned.
     */
    video.load();

    video.dataset.brdVideoLoaded = '1';

    return true;
  }


  function playVideo(video) {
    if (!video) {
      return;
    }

    if (!ensureVideoLoaded(video)) {
      return;
    }

    var promise = video.play();

    if (
      promise &&
      typeof promise.catch === 'function'
    ) {
      promise.catch(function () {
        /*
         * Autoplay may be blocked by the browser.
         * The poster remains visible, which is an acceptable fallback.
         */
      });
    }
  }


  function pauseVideo(video) {
    if (!video) {
      return;
    }

    video.pause();
  }


  /* ----------------------------------------------------------
   * Initialise one hero slider
   * ---------------------------------------------------------- */

  function initHeroSlider(root) {
    if (
      !root ||
      root.dataset.brdHeroInit === '1'
    ) {
      return;
    }

    whenSwiperReady(function () {
      var swiperEl = root.querySelector('.brd-swiper');

      if (!swiperEl) {
        return;
      }


      var autoplay =
        root.dataset.autoplay === 'true';

      var autoplayMs =
        parseInt(root.dataset.autoplayDelay, 10) ||
        5000;

      var loop =
        root.dataset.loop === 'true';

      var showArrows =
        root.dataset.showArrows === 'true';

      var showDots =
        root.dataset.showDots === 'true';

      var slideCount =
        swiperEl.querySelectorAll('.swiper-slide').length;


      var prefersReducedMotion =
        window.matchMedia &&
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;


      /* ------------------------------------------------------
       * Swiper
       * ------------------------------------------------------ */

      var instance = new window.Swiper(swiperEl, {
        slidesPerView: 1,

        spaceBetween: 0,

        loop:
          loop &&
          slideCount > 1,

        speed: 700,

        autoplay:
          autoplay &&
          slideCount > 1 &&
          !prefersReducedMotion
            ? {
                delay: autoplayMs,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
              }
            : false,

        pagination:
          showDots
            ? {
                el: root.querySelector(
                  '.brd-hero-slider__pagination'
                ),

                clickable: true,

                renderBullet: function (
                  index,
                  className
                ) {
                  var label =
                    root.dataset.slideLabel ||
                    'Go to slide';

                  return (
                    '<button type="button" class="' +
                    className +
                    '" aria-label="' +
                    label +
                    ' ' +
                    (index + 1) +
                    '"></button>'
                  );
                }
              }
            : false,

        navigation:
          showArrows
            ? {
                nextEl: root.querySelector(
                  '.brd-hero-slider__arrow-next'
                ),

                prevEl: root.querySelector(
                  '.brd-hero-slider__arrow-prev'
                )
              }
            : false,

        a11y: {
          enabled: true,

          prevSlideMessage:
            root.dataset.prevLabel ||
            'Previous slide',

          nextSlideMessage:
            root.dataset.nextLabel ||
            'Next slide'
        },

        keyboard: {
          enabled: true,
          onlyInViewport: true
        },

        watchSlidesProgress: true,

        grabCursor: true
      });


      /* ------------------------------------------------------
       * Video synchronisation
       * ------------------------------------------------------ */

      function syncVideoPlayback() {
        var wantedVariant =
          isMobileViewport()
            ? 'mobile'
            : 'desktop';


        var videos =
          swiperEl.querySelectorAll(
            '.brd-hero-slider__video'
          );


        videos.forEach(function (video) {
          var slide =
            video.closest('.swiper-slide');

          if (!slide) {
            pauseVideo(video);
            return;
          }


          var isActive =
            slide.classList.contains(
              'swiper-slide-active'
            );


          var variant =
            video.dataset.brdVideoVariant;


          var correctViewport =
            variant === wantedVariant;


          /*
           * Only one video should ever be allowed to start:
           *
           * active slide
           * +
           * correct viewport
           */
          if (isActive && correctViewport) {
            playVideo(video);
          } else {
            pauseVideo(video);
          }
        });
      }


      /*
       * Start loading the incoming active video's correct
       * variant as soon as the transition begins.
       */
      instance.on(
        'slideChangeTransitionStart',
        syncVideoPlayback
      );


      /*
       * Run again after transition to make absolutely sure
       * only the final active slide remains playing.
       */
      instance.on(
        'slideChangeTransitionEnd',
        syncVideoPlayback
      );


      /*
       * Initial page state.
       *
       * THIS is when the first active video finally receives
       * its src.
       */
      syncVideoPlayback();


      /* ------------------------------------------------------
       * Responsive breakpoint changes
       *
       * Mainly useful for desktop browser resizing and Shopify
       * Theme Editor previews.
       * ------------------------------------------------------ */

      var viewportQuery =
        window.matchMedia
          ? window.matchMedia(MOBILE_QUERY)
          : null;


      function handleViewportChange() {
        syncVideoPlayback();
      }


      if (viewportQuery) {
        if (
          typeof viewportQuery.addEventListener ===
          'function'
        ) {
          viewportQuery.addEventListener(
            'change',
            handleViewportChange
          );
        } else if (
          typeof viewportQuery.addListener ===
          'function'
        ) {
          // Older Safari.
          viewportQuery.addListener(
            handleViewportChange
          );
        }
      }


      /* ------------------------------------------------------
       * Page visibility
       * ------------------------------------------------------ */

      function handleVisibilityChange() {
        if (document.hidden) {
          /*
           * Stop Swiper autoplay.
           */
          if (instance.autoplay) {
            instance.autoplay.stop();
          }

          /*
           * Also pause video playback.
           */
          swiperEl
            .querySelectorAll(
              '.brd-hero-slider__video'
            )
            .forEach(pauseVideo);

        } else {

          if (instance.autoplay) {
            instance.autoplay.start();
          }

          syncVideoPlayback();
        }
      }


      document.addEventListener(
        'visibilitychange',
        handleVisibilityChange
      );


      /* ------------------------------------------------------
       * Store cleanup hooks on the root
       * ------------------------------------------------------ */

      root.brdHeroCleanup = function () {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );


        if (viewportQuery) {
          if (
            typeof viewportQuery.removeEventListener ===
            'function'
          ) {
            viewportQuery.removeEventListener(
              'change',
              handleViewportChange
            );
          } else if (
            typeof viewportQuery.removeListener ===
            'function'
          ) {
            viewportQuery.removeListener(
              handleViewportChange
            );
          }
        }
      };


      /*
       * Expose Swiper instance for Shopify Theme Editor.
       */
      swiperEl.brdSwiper = instance;

      root.dataset.brdHeroInit = '1';
    });
  }


  /* ----------------------------------------------------------
   * Boot all hero sliders
   * ---------------------------------------------------------- */

  function bootAll() {
    document
      .querySelectorAll(
        '.brd-hero-slider[data-section-id]'
      )
      .forEach(initHeroSlider);
  }


  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      bootAll
    );

  } else {

    bootAll();

  }


  /* ----------------------------------------------------------
   * Shopify Theme Editor:
   * section load
   * ---------------------------------------------------------- */

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      var section =
        event.target.querySelector(
          '.brd-hero-slider[data-section-id]'
        );

      if (section) {
        initHeroSlider(section);
      }
    }
  );


  /* ----------------------------------------------------------
   * Shopify Theme Editor:
   * block select
   * ---------------------------------------------------------- */

  document.addEventListener(
    'shopify:block:select',
    function (event) {
      var hero =
        event.target.closest(
          '.brd-hero-slider[data-section-id]'
        );

      if (!hero) {
        return;
      }


      var slideEl =
        event.target.closest('.swiper-slide');

      if (!slideEl) {
        return;
      }


      var swiperEl =
        hero.querySelector('.brd-swiper');

      if (
        !swiperEl ||
        !swiperEl.brdSwiper
      ) {
        return;
      }


      var index =
        Array.prototype.indexOf.call(
          slideEl.parentNode.children,
          slideEl
        );


      if (
        typeof swiperEl.brdSwiper.slideToLoop ===
        'function'
      ) {

        swiperEl.brdSwiper.slideToLoop(index);

      } else {

        swiperEl.brdSwiper.slideTo(index);

      }


      if (swiperEl.brdSwiper.autoplay) {
        swiperEl.brdSwiper.autoplay.stop();
      }
    }
  );


  /* ----------------------------------------------------------
   * Shopify Theme Editor:
   * section unload
   * ---------------------------------------------------------- */

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      var section =
        event.target.querySelector(
          '.brd-hero-slider[data-section-id]'
        );

      if (!section) {
        return;
      }


      if (
        typeof section.brdHeroCleanup ===
        'function'
      ) {
        section.brdHeroCleanup();
      }


      var swiperEl =
        section.querySelector('.brd-swiper');


      if (
        swiperEl &&
        swiperEl.brdSwiper &&
        typeof swiperEl.brdSwiper.destroy ===
          'function'
      ) {

        swiperEl.brdSwiper.destroy(
          true,
          true
        );

        swiperEl.brdSwiper = null;
      }
    }
  );

})();